import { Link } from "react-router-dom";
import { Trash2, Clock, CheckSquare, Square, FilePlus2, FileText } from "lucide-react";
import { formatMoney } from "../../lib/currency";

const FLAGS = { it: "🇮🇹", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸", pl: "🇵🇱" };
const STATUS_CLASSES = {
  sent:     "bg-blue-500/10 text-blue-300 border-blue-500/40",
  viewed:   "bg-violet-500/10 text-violet-300 border-violet-500/40",
  accepted: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  rejected: "bg-red-500/10 text-red-300 border-red-500/40",
};

function StatusPill({ status, t }) {
  const cls = STATUS_CLASSES[status];
  if (!cls) return null;
  return <span className={`text-[9px] label-tech px-2 py-0.5 rounded border ${cls}`}>{t(`status.${status}`)}</span>;
}

export function EmptyState({ t }) {
  return (
    <div className="p-16 text-center" data-testid="empty-state">
      <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
        <FileText size={28} className="text-blue-400"/>
      </div>
      <div className="font-display font-semibold text-slate-100 text-lg">{t("dashboard.empty")}</div>
      <div className="text-slate-500 text-sm mt-2 mb-6">{t("dashboard.emptyHint")}</div>
      <Link to="/new-quote" className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-md">
        <FilePlus2 size={16}/> {t("dashboard.start")}
      </Link>
    </div>
  );
}

export default function QuoteRow({ q, selected, onToggle, onRemove, t, locale, isNew = false }) {
  return (
    <div className={`px-6 py-4 transition-colors flex items-center gap-4 ${selected ? "bg-blue-500/5" : isNew ? "bg-emerald-500/5 hover:bg-slate-800/40" : "hover:bg-slate-800/40"}`} data-testid={`quote-row-${q.id}`}>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(q.id); }} className="text-slate-500 hover:text-blue-400" data-testid={`select-quote-${q.id}`}>
        {selected ? <CheckSquare size={18} className="text-blue-400"/> : <Square size={18}/>}
      </button>
      <Link to={`/quotes/${q.id}`} className="flex-1 min-w-0 cursor-pointer">
        <div className="font-display text-slate-100 font-medium truncate flex items-center gap-2">
          {isNew && <span className="text-[9px] label-tech px-2 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/40" data-testid={`quote-new-badge-${q.id}`}>{t("analysis.newBadge")}</span>}
          {q.analysis?.part_description || t("dashboard.noDescription")}
          {q.status && q.status !== "draft" && <StatusPill status={q.status} t={t}/>}
        </div>
        <div className="mt-1 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
          <span className="font-mono-data">{q.material_name}</span>
          <span>·</span>
          <span>{t("dashboard.lot")} {q.batch_size}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock size={12}/> {q.calculation?.totals?.cycle_time_minutes_per_piece?.toFixed(1)}&apos;/pz</span>
          {q.customer?.name && (<><span>·</span><span className="text-blue-300">{q.customer.name}</span></>)}
          {q.source === "time_study_pdf" && (<><span>·</span><span className="text-amber-300 text-[10px] label-tech">PDF</span></>)}
          {q.detected_language && q.source === "time_study_pdf" && (
            <span className="text-[10px] label-tech text-slate-400" title={t("dashboard.detectedLang")}>
              {FLAGS[q.detected_language] || "🌍"}
            </span>
          )}
          <span>·</span>
          <span>{new Date(q.created_at).toLocaleString(locale)}</span>
        </div>
      </Link>
      <Link to={`/quotes/${q.id}`} className="text-right cursor-pointer">
        <div className="font-mono-data text-lg text-blue-400">
          {formatMoney(q.calculation?.totals?.total_eur || 0, q.currency || "EUR")}
        </div>
        <div className="text-[10px] label-tech mt-1">{t("dashboard.per")}</div>
      </Link>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(q.id); }} className="ml-2 p-2 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-quote-${q.id}`}>
        <Trash2 size={16}/>
      </button>
    </div>
  );
}
